"use client";

import { useEffect, useState } from "react";

export interface GrantedCoords {
  latitude: number;
  longitude: number;
}

/**
 * 이미 위치 권한이 `granted` 인 경우에만 조용히 좌표를 읽는다.
 *
 * "가까운 지점" 같은 **부가** 정렬 때문에 권한 프롬프트를 띄우면 안 된다
 * (홈의 NearbyProductsSection 과 같은 정책). 권한이 prompt/denied 이거나
 * Permissions API 미지원이면 `null` 을 유지하고, 호출부는 해당 UI를 건너뛴다.
 *
 * 브라우저 `navigator.geolocation` 만 사용한다 — 네이티브(Capacitor) 앱도
 * WebView 의 Permissions API 를 그대로 쓰므로 권한이 이미 허용된 상태라면
 * 동일하게 동작하고, 아니면 조용히 건너뛴다.
 */
export function useGrantedGeolocation(enabled: boolean = true): GrantedCoords | null {
  const [coords, setCoords] = useState<GrantedCoords | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined") return;
    if (!navigator.permissions || !navigator.geolocation) return;

    let cancelled = false;

    // 비동기 콜백 안에서만 setState — effect 본문에서 동기 setState 하지 않는다.
    void (async () => {
      try {
        const permission = await navigator.permissions.query({
          name: "geolocation" as PermissionName,
        });
        if (cancelled || permission.state !== "granted") return;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (cancelled) return;
            setCoords({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          () => {
            /* 이미 허용된 권한에서도 실패할 수 있다 — 조용히 무시 */
          },
          { timeout: 3000, maximumAge: 300000, enableHighAccuracy: false },
        );
      } catch {
        /* Permissions API 미지원 브라우저 — 무시 */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return coords;
}
